import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EXPERT_REPORT_COMPARE_WITH,
  ExpertReportHeaderActions,
  ExpertReportPeriodPicker,
  ExpertReportSubtitle,
  useExpertReportPeriod,
} from '@features/expertReports'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import type { PeriodMode } from '../../enums/periodMode'
import type { PeriodValue } from '../../types/periodValue'
import { ExpertProfitAndLossResultHeader } from './elements/ExpertProfitAndLossResultHeader'
import { ExpertProfitAndLossTable } from './elements/ExpertProfitAndLossTable'
import { ExpertProfitAndLossToolbar } from './elements/ExpertProfitAndLossToolbar'
import { useExpertProfitAndLossSearchParam } from './hooks/useExpertProfitAndLossSearchParam'
import { useFetchExpertProfitAndLoss } from './hooks/useFetchExpertProfitAndLoss'
import { EXPERT_PROFIT_AND_LOSS_COMPARE_COUNT } from './query-api'
import { ProfitAndLossContent } from './styles'
import type { ExpertProfitAndLossReport } from './types/expertProfitAndLossReport'
import { selectProfitAndLossView } from './utils/selectProfitAndLossView'

interface ExpertProfitAndLossContentProps {
  currencyId?: string
  encodedPeriod?: string
  isError?: boolean
  isFetchingNewPeriod?: boolean
  isLoading?: boolean
  periodType?: PeriodMode
  report?: ExpertProfitAndLossReport
  search?: string
  searchTerm?: string
  setPeriod?: (value: PeriodValue) => boolean
  setSearch?: (value: string) => void
  /** Same shape as `report`, but must stay unaffected by `search` - drives the result header. */
  unfilteredReport?: ExpertProfitAndLossReport
}

export const ExpertProfitAndLossContent = ({
  currencyId,
  encodedPeriod = '',
  isError = false,
  isFetchingNewPeriod = false,
  isLoading = false,
  periodType,
  report,
  search = '',
  searchTerm = '',
  setPeriod = () => false,
  setSearch = () => undefined,
  unfilteredReport,
}: ExpertProfitAndLossContentProps): ReactElement => {
  const { t } = useTranslation()
  const viewData = useMemo(() => selectProfitAndLossView(report), [report])

  const resultAmount = useMemo((): number | undefined => {
    if (isLoading || isError) {
      return undefined
    }

    const unfilteredViewData = selectProfitAndLossView(unfilteredReport)
    // Operating result and pre-tax result are also root-level `kind === 'result'` rows - the net result is always the last one.
    const resultRows = unfilteredViewData.rows.filter((row) => row.kind === 'result')

    return resultRows.at(-1)?.totals[0]
  }, [isError, isLoading, unfilteredReport])

  const headerBottomActions = (
    <ExpertReportHeaderActions>
      <ExpertProfitAndLossToolbar search={search} setSearch={setSearch} />
    </ExpertReportHeaderActions>
  )
  const sideActions = <ExpertReportPeriodPicker encodedPeriod={encodedPeriod} onChange={setPeriod} />

  const subtitle = resultAmount !== undefined && (
    <ExpertReportSubtitle>
      <ExpertProfitAndLossResultHeader currencyId={currencyId} value={resultAmount} />
    </ExpertReportSubtitle>
  )

  return (
    <ModuleLayout
      fullHeight
      fullWidth
      headerBottomActions={headerBottomActions}
      sideActions={sideActions}
      subtitle={subtitle}
      title={t('expert_profit_and_loss.title')}
    >
      <ProfitAndLossContent>
        <ExpertProfitAndLossTable
          isError={isError}
          isLoading={isLoading || isFetchingNewPeriod}
          periods={viewData.periods}
          periodType={periodType}
          rows={viewData.rows}
          searchTerm={searchTerm}
        />
      </ProfitAndLossContent>
    </ModuleLayout>
  )
}

export const ExpertProfitAndLoss = (): ReactElement => {
  const { organization } = useUserOrganization()
  const { encodedPeriod, periodType, setPeriod } = useExpertReportPeriod()
  const { search, setSearch } = useExpertProfitAndLossSearchParam()
  const { displayedSearch, isError, isFetchingNewPeriod, isLoading, report, unfilteredReport } =
    useFetchExpertProfitAndLoss({
      compareCount: EXPERT_PROFIT_AND_LOSS_COMPARE_COUNT,
      compareWith: EXPERT_REPORT_COMPARE_WITH,
      organizationId: organization?.id ?? '',
      period: encodedPeriod,
      search: search || undefined,
    })

  return (
    <ExpertProfitAndLossContent
      currencyId={organization?.baseCurrencyId}
      encodedPeriod={encodedPeriod}
      isError={isError}
      isFetchingNewPeriod={isFetchingNewPeriod}
      isLoading={isLoading}
      periodType={periodType}
      report={report}
      search={search}
      searchTerm={displayedSearch}
      setPeriod={setPeriod}
      setSearch={setSearch}
      unfilteredReport={unfilteredReport}
    />
  )
}
